/* File: src/components/account/AccountSubscriptionSection.js */
import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCard, faCalendarAlt, faCrown, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import { PRICE_IDS } from '../../constants/stripeConstants'; // Ensure this path is correct

const AccountSubscriptionSection = ({
    isContextLoading,
    userSubscriptionStatus,
    userSubscriptionEndDate,
    userSubscriptionPriceId,
    isPortalLoading,
    portalError,
    handleManageBilling,
    createCustomerPortalSession, // Function reference to check availability
    // UI Components passed as props
    ButtonComponent,
    AlertComponent,
    StatusBadgeComponent,
    LoadingSpinnerComponent, // For the inline loading message
}) => {
    // Use the passed-in components.
    const Button = ButtonComponent;
    const Alert = AlertComponent;
    const StatusBadge = StatusBadgeComponent;
    const LoadingSpinner = LoadingSpinnerComponent;

    // Check if essential UI components are passed
    if (!Button || !Alert || !StatusBadge || !LoadingSpinner) {
        console.error("AccountSubscriptionSection: Required UI component props were not provided.");
        return <div className="text-red-500 p-4">Error: UI components for this section are missing.</div>;
    }

    // --- Helper functions moved from AccountPage.js ---
    const formatDate = (date) => {
        if (!date) return "";
        return date.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const getPlanName = useCallback(() => {
        if (userSubscriptionPriceId === PRICE_IDS.monthly) {
            return "Monthly Plan";
        } else if (userSubscriptionPriceId === PRICE_IDS.yearly) {
            return "Yearly Plan";
        } else if (userSubscriptionPriceId) {
            return "Subscribed Plan"; // Generic for other known price IDs if any
        }
        return "Plan"; // Fallback
    }, [userSubscriptionPriceId]);

    // --- Main render logic for subscription info (was renderSubscriptionInfo) ---
    const renderSubscriptionDetails = () => {
        if (isContextLoading || userSubscriptionStatus === 'loading' || userSubscriptionStatus === null) {
            return (
                <p className="text-sm text-gray-500 italic flex items-center">
                    <LoadingSpinner whiteText={false} /> {/* Assuming LoadingSpinner takes whiteText prop */}
                    Loading subscription status...
                </p>
            );
        }

        if (userSubscriptionStatus === 'error') {
            return <p className="text-sm text-red-600">Could not load subscription status.</p>;
        }

        const endDateStr = userSubscriptionEndDate ? formatDate(userSubscriptionEndDate) : "";
        const planName = getPlanName();

        switch (userSubscriptionStatus) {
            case 'trialing':
                return (
                    <div>
                        <div className="mb-3">
                            <h4 className="font-semibold text-lg text-gray-800 mb-1">Trial Active</h4>
                            <p className="text-sm text-gray-600">{planName}</p>
                            {endDateStr && (
                                <div className="mt-2 text-sm text-gray-600">
                                    <FontAwesomeIcon icon={faCalendarAlt} className="mr-2 text-indigo-500" />
                                    Trial ends {endDateStr}
                                </div>
                            )}
                        </div>
                        <p className="text-sm text-gray-600 mt-2">
                            Enjoy your trial period! Access all premium features.
                        </p>
                    </div>
                );

            case 'active':
                return (
                    <div>
                        <div className="mb-3">
                            <div className="flex items-center mb-1">
                                <StatusBadge type="success" text="Active" />
                                <span className="ml-2 font-semibold text-lg text-gray-800">{planName}</span>
                            </div>
                            {endDateStr && (
                                <div className="text-sm text-gray-600">
                                    <FontAwesomeIcon icon={faCalendarAlt} className="mr-2 text-indigo-500" />
                                    Renews {endDateStr}
                                </div>
                            )}
                        </div>
                        <p className="text-sm text-gray-600 mt-2">
                            Thank you for your subscription! Enjoy all premium features.
                        </p>
                    </div>
                );

            case 'canceled':
                if (endDateStr && userSubscriptionEndDate && userSubscriptionEndDate > new Date()) {
                    return (
                        <div>
                            <div className="mb-3">
                                <div className="flex items-center mb-1">
                                    <StatusBadge type="warning" text="Canceled" />
                                    <span className="ml-2 font-semibold text-lg text-gray-800">{planName}</span>
                                </div>
                                {endDateStr && (
                                    <div className="text-sm text-gray-600">
                                        <FontAwesomeIcon icon={faCalendarAlt} className="mr-2 text-indigo-500" />
                                        Access until {endDateStr}
                                    </div>
                                )}
                            </div>
                            <p className="text-sm text-gray-600 mt-2">
                                Your subscription has been canceled but you still have access until the end date.
                            </p>
                        </div>
                    );
                } else {
                    return (
                        <div className="flex flex-col space-y-3">
                            <div className="flex items-center">
                                <StatusBadge type="error" text="Inactive" />
                                <span className="ml-2 text-sm text-gray-700">Subscription ended</span>
                            </div>
                            <Link to="/resubscribe" className="text-sm text-indigo-600 hover:text-indigo-800 hover:underline flex items-center">
                                <FontAwesomeIcon icon={faCreditCard} className="mr-1.5 h-3.5 w-3.5" />
                                View Plans to Resubscribe
                            </Link>
                        </div>
                    );
                }

            case 'past_due':
            case 'unpaid':
                return (
                    <div className="flex flex-col space-y-3">
                        <div className="flex items-center">
                            <StatusBadge type="error" text="Payment Issue" />
                            <span className="ml-2 text-sm text-gray-700">{planName} • Action required</span>
                        </div>
                        <p className="text-sm text-red-600 flex items-start">
                            <FontAwesomeIcon icon={faExclamationCircle} className="mr-1.5 h-4 w-4 mt-0.5 flex-shrink-0" />
                            We couldn't process your latest payment. Please update your payment method below.
                        </p>
                        <Button
                            onClick={handleManageBilling}
                            disabled={isPortalLoading || !createCustomerPortalSession}
                            isLoading={isPortalLoading}
                            loadingText="Loading..."
                            text="Update Payment Method"
                            color="indigo"
                            icon={faCreditCard}
                        />
                    </div>
                );

            case 'incomplete':
                return (
                    <div className="flex flex-col space-y-3">
                        <div className="flex items-center">
                            <StatusBadge type="warning" text="Incomplete" />
                            <span className="ml-2 text-sm text-gray-700">{planName} • Payment processing</span>
                        </div>
                        <Button
                            onClick={handleManageBilling}
                            disabled={isPortalLoading || !createCustomerPortalSession}
                            isLoading={isPortalLoading}
                            loadingText="Loading..."
                            text="Manage Billing"
                            color="blue"
                            icon={faCreditCard}
                        />
                    </div>
                );

            case 'incomplete_expired':
                return (
                    <div className="flex flex-col space-y-3">
                        <div className="flex items-center">
                            <StatusBadge type="error" text="Expired" />
                            <span className="ml-2 text-sm text-gray-700">Subscription attempt failed</span>
                        </div>
                        <Link to="/resubscribe" className="text-sm text-indigo-600 hover:text-indigo-800 hover:underline flex items-center">
                            <FontAwesomeIcon icon={faCreditCard} className="mr-1.5 h-3.5 w-3.5" />
                            Choose a Plan to Try Again
                        </Link>
                    </div>
                );

            case 'no_subscription':
            case 'inactive': // Note: 'inactive' also covered in 'canceled' if access ended. This is a fallback.
            default:
                return (
                    <div className="flex flex-col space-y-3">
                        <div className="flex items-center">
                            <StatusBadge type="neutral" text="No Subscription" />
                        </div>
                        <Link to="/resubscribe" className="text-sm text-indigo-600 hover:text-indigo-800 hover:underline flex items-center">
                            <FontAwesomeIcon icon={faCrown} className="mr-1.5 h-3.5 w-3.5" />
                            View Plans to Subscribe
                        </Link>
                    </div>
                );
        }
    };

    // Determine if the main manage billing button should be shown
    const showManageButton = (
        userSubscriptionStatus === 'active' ||
        userSubscriptionStatus === 'trialing' ||
        (userSubscriptionStatus === 'canceled' && userSubscriptionEndDate && userSubscriptionEndDate > new Date())
    ) && createCustomerPortalSession; // Check if the function itself is available

    return (
        <>
            {/* Main Content Area - Renders based on status */}
            <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200 min-h-[60px]">
                {renderSubscriptionDetails()}
            </div>

            {/* Alert for portal errors */}
            <Alert type="error" message={portalError} />

            {/* Manage Button Area - Only shown for specific statuses */}
            {showManageButton && (
                <div className="flex justify-end mt-4">
                    <div className="flex flex-col items-end">
                        <Button
                            onClick={handleManageBilling}
                            disabled={isPortalLoading} // Disabled state is now primarily controlled by isPortalLoading
                            isLoading={isPortalLoading}
                            loadingText="Opening Portal..."
                            text="Manage Billing & Invoices"
                            color="indigo"
                            icon={faCreditCard}
                            fullWidth={false}
                        />
                        <p className="text-xs text-gray-500 text-right mt-1">
                            Access your payment history, update payment methods, and manage your subscription.
                        </p>
                    </div>
                </div>
            )}

            {/* Message if button should show but function isn't available */}
            {!createCustomerPortalSession &&
                (userSubscriptionStatus === 'active' ||
                 userSubscriptionStatus === 'trialing' ||
                 (userSubscriptionStatus === 'canceled' &&
                  userSubscriptionEndDate &&
                  userSubscriptionEndDate > new Date())) && (
                <p className="text-sm text-gray-500 italic bg-gray-50 p-3 rounded-md border border-gray-200">
                    <FontAwesomeIcon icon={faExclamationCircle} className="mr-2 text-gray-400" />
                    Billing management is temporarily unavailable.
                </p>
            )}
        </>
    );
};

export default AccountSubscriptionSection;